import { MobXProviderContext } from "mobx-react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Rating } from "primereact/rating";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useRef, useState } from "react";
import { getErrorTitle } from "../../../constants/constants";
import { convertIsoToDate } from "../../../utils/stringUtils";
import { AsyncProfileImageInDataTableRow } from "../../home/components/AsyncProfileImageInDataTableRow";

const ReviewPage = () => {
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const [loading, setLoading] = useState(false);
  const [allReviews, setAllReviews] = useState<any[] | null>(null);
  const toast = useRef<Toast>(null);
  const [deleteFlag, setDeleteFlag] = useState(false);
  const userId = authStore.getUserId();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.listAllReview(userId);
        setAllReviews(response);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: "Failed to fetch reviews.",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [backendAPIStore, deleteFlag, userId]);

  const onDeleteReview = async (reviewId: string) => {
    confirmDialog({
      message: "Are you sure you want to delete this review?",
      header: "Confirmation",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger p-button-outlined",
      accept: async () => {
        try {
          setDeleteFlag(true);
          await backendAPIStore.init();
          await backendAPIStore.deleteReview(reviewId);
          setAllReviews(allReviews?.filter((review) => review.reviewId !== reviewId) || null);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Review deleted successfully.",
            life: 3000,
          });
        } catch (error) {
          console.error("Error deleting review:", error);
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: "Failed to delete review.",
            life: 3000,
          });
        } finally {
          setDeleteFlag(false);
        }
      },
    });
  };

  const titleTemplate = (title: string) => (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <span className="text-xl text-900" style={{ textTransform: "capitalize" }}>
        {title}
      </span>
    </div>
  );

  const userProfileTemplate = (rowData: any) => (
    <div className="justify-content-center align-items-center d-flex flex-wrap grid">
      <div className="col flex-column flex-wrap" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
        <AsyncProfileImageInDataTableRow userId={rowData.userId} />
        <div style={{ textTransform: "capitalize" }}>
          {rowData.userProfile.firstName} {rowData.userProfile.lastName}
        </div>
        <div style={{ textTransform: "capitalize" }}>
          <strong>{rowData.userProfile.userRoleType}</strong>
        </div>
      </div>
      <div className="col flex-column flex-wrap" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
        <div style={{ whiteSpace: "pre-wrap", maxWidth: "400px" }}>{rowData.reviewComment}</div>
        <Rating value={rowData.reviewStar} readOnly cancel={false} className="mt-2" />
      </div>
      <div className="col flex-column flex-wrap" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
        <label>Added on: {convertIsoToDate(rowData.createdAt)}</label>
        <i
          className="pi pi-trash ml-2"
          style={{ color: "red", cursor: "pointer" }}
          onClick={() => onDeleteReview(rowData.reviewId)}
        />
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component">
      <Toast ref={toast} />
      {loading ? (
        <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
      ) : (
        <Card title={titleTemplate(`Reviews submitted by you: ${allReviews ? allReviews.length : 0}`)}>
          <DataTable
            value={allReviews!}
            emptyMessage="No reviews found. Please add a review of any user from your home."
            showHeaders={false}
          >
            <Column header="User" body={userProfileTemplate} />
          </DataTable>
        </Card>
      )}
    </div>
  );
};

export default ReviewPage;
